import type { AssessmentType } from '../apiClient';

/**
 * The unlock panel on a free result, copied from production's three results
 * pages (`results.tsx`, `work-fit-results.tsx`, `perspective-results.tsx`
 * at `11ca09a`).
 *
 * **Why this exists.** V2's free result offered one generic sentence — "The
 * full report includes your complete breakdown, a step-by-step plan, and
 * printable workbooks" — where production names eight or nine specific things,
 * differently for each assessment type. That sentence was V2's, not
 * production's, and it is the copy a customer reads at the moment they decide
 * whether to pay.
 *
 * **The one sanctioned divergence.** Production is currently early-access
 * gated: below these bullets it renders a promo-code panel rather than a
 * checkout. V2 keeps its paid checkout architecture by Product Owner decision
 * (2026-08-29), so the bullets, the headings and the lead paragraphs are
 * production's verbatim and only the action beneath them differs.
 *
 * Every string below is transcribed from production and verified against it by
 * `test/unlockContent.test.ts`.
 */
export interface UnlockContent {
  /**
   * The bridge line production prints above the panel. Only the Bottleneck page
   * has one; the other two fold the same idea into `lead`.
   */
  bridge?: string;
  /** Panel heading while the report is locked. */
  lockedHeading: string;
  /** The paragraph beneath the locked heading. */
  lead: string;
  /** What the full report contains. Production's order. */
  bullets: readonly string[];
  /** The italic line under the bullets. Bottleneck only. */
  footnote?: string;
  /** Panel heading once the report is unlocked. */
  unlockedHeading: string;
  /** The paragraph beneath the unlocked heading. */
  unlockedLead: string;
  /** The button that opens the report. */
  unlockedAction: string;
}

export const UNLOCK_CONTENT: Record<AssessmentType, UnlockContent> = {
  bottleneck: {
    bridge:
      'This free result shows the pattern that is creating drag. What it does not show yet is why this pattern developed, where it is quietly costing you the most, what false belief may be keeping it in place, and what to change over the next 7 and 30 days.',
    lockedHeading: 'Unlock Your Full Bottleneck Reset Plan',
    lead: 'Your free result shows the pattern. The full report shows why it keeps happening, what it is costing you, and how to reset it over the next 7 and 30 days.',
    bullets: [
      'Why this pattern developed: the root cause behind your bottleneck type',
      'What it is costing you: performance, time, and energy impact analysis',
      'False belief and sabotage pattern: the thinking keeping this locked in place',
      'Stop doing and start doing: 8 behavior-level changes specific to your type',
      '7-day reset journal: one guided day at a time with daily prompts',
      '30-day reset workbook: weekly themes, habits, and reflections',
      'PDF export: print your full workbook and take it anywhere',
      'Discounted reassessment: retake after 30 days and compare your scores',
    ],
    footnote:
      'Early users have described these reports as eye-opening and more accurate than expected.',
    /*
      PARITY TODO — the only strings on this page that are not production's.
      Production's Bottleneck results page has no unlocked branch at all,
      because it is early-access gated and nobody there has ever unlocked one.
      These follow the shape its Work Fit and Perspective pages use, with the
      wording carried across. Replace them if production ever grows the branch.
    */
    unlockedHeading: 'Full Bottleneck Report',
    unlockedLead:
      'Your full report includes a deep dive on your pattern, what it is costing you, the false belief keeping it in place, behaviour-level changes for your type, a 7-day reset journal, and a 30-day reset workbook.',
    unlockedAction: 'View Full Bottleneck Report',
  },

  work_fit: {
    lockedHeading: 'Full Work Fit Report',
    lead: 'This free result shows the kind of environment you are likely built for. What it does not show yet is how each part of your work environment is affecting your performance, where your current role may be draining you, what to look for in a better-fit role, and how to evaluate a manager, team, or company before you commit more energy to it.',
    bullets: [
      'Full environment fit profile: what you need, what drains you, and how you evaluate roles',
      'Per-dimension work fit analysis with good fit and poor fit signals',
      'Manager fit guide with compatibility signals and warning signs',
      'Role and culture risk signals specific to your profile',
      'Categorized interview question bank calibrated to your top fit needs',
      'Offer evaluation checklist across 10 dimensions',
      '30/60/90 success conditions framework for any role you are evaluating',
      'PDF export: take your report anywhere',
    ],
    unlockedHeading: 'Full Work Fit Report',
    unlockedLead:
      'Your full report includes a deep dive on your profile, per-dimension environment analysis, a manager fit guide, culture risk signals, a categorized interview question bank, an offer evaluation checklist, and a 30/60/90 success conditions framework.',
    unlockedAction: 'View Full Work Fit Report',
  },

  perspective: {
    lockedHeading: 'Full Perspective Reset',
    lead: 'This free result shows the lens that is most active right now. What it does not show yet is how that lens shapes your self-talk, your time orientation, your decisions under pressure, and the interpretation traps that may be keeping the pattern active.',
    bullets: [
      'Full 8-pattern map with primary and secondary perspective patterns',
      'Self-talk analysis: the internal narrative your pattern generates',
      'Time orientation: how this pattern affects your past, present, and future thinking',
      'Belief friction: the underlying belief driving the pattern',
      'Interpretation traps: flags specific to your pattern and your answers',
      'ABCDE reframe exercise tailored to your dominant pattern',
      '7-day reset plan with daily focus and journal prompts',
      '30-day practice plan with weekly themes and goals',
      'PDF export: take your report anywhere',
    ],
    /* Production's unlocked heading is "Full Perspective Report" — note that it
       differs from its own locked heading, "Full Perspective Reset". */
    unlockedHeading: 'Full Perspective Report',
    unlockedLead:
      'Your full report includes a deep dive on your pattern, self-talk analysis, time orientation, belief friction, interpretation traps, an ABCDE reframe exercise, a 7-day reset plan, and a 30-day practice plan.',
    unlockedAction: 'View Full Perspective Report',
  },
};
